import {MaterialNode} from "#material/nodes/base/material/material-node"
import type {MappingSockets} from "#material/nodes/vector/mapping/definition"
import {mappingDefinition} from "#material/nodes/vector/mapping/definition"
import {cyclesMappingType, getMappingMatrix} from "#material/nodes/vector/mapping/helpers"
import type {CyclesReturns} from "#material/sockets/cycles"
import type {CyclesEvaluator, ThreeEvaluator} from "#material/nodes/base/material/types/evaluators"
import {registerMaterialGraphNode} from "#material/register"
import {ThreeTsl as TSL} from "#material/three/exports"
import type {ThreeReturns} from "#material/sockets/three"
import {cyclesVec3, cyclesVec3WithNullableComponents} from "#material/cycles/helpers"
import type {MappingNode} from "#material/cycles/types"

@registerMaterialGraphNode
export class Mapping extends MaterialNode<MappingSockets> {
    public override get definition() {
        return mappingDefinition
    }

    override toThree = async (evaluator: ThreeEvaluator): Promise<ThreeReturns<MappingSockets>> => {
        const vector = await evaluator.get(this.parameters.inputs.vector)
        const {logger} = evaluator.context
        if (!vector) logger.warn("Input to Mapping node not connected, defaulting to UV!")

        const vectorValue = vector ? vector.toVec3() : TSL.vec3(TSL.uv(0), 0)
        const matrix = getMappingMatrix(this.parameters.settings)

        const matrixNode = TSL.mat4(matrix)
        const transformedVector = matrixNode.mul(TSL.vec4(vectorValue.x, vectorValue.y, vectorValue.z, 1.0)).xyz
        const outputVector = this.parameters.settings.vectorType === "NORMAL" ? TSL.normalize(transformedVector) : transformedVector

        return {vector: outputVector}
    }

    override toCycles = async (evaluator: CyclesEvaluator): Promise<CyclesReturns<MappingSockets>> => {
        const mappingNode: MappingNode = {
            type: "mapping",
            inputs: await evaluator.getAll({
                vector: this.parameters.inputs.vector,
                location: this.parameters.inputs.location,
                rotation: this.parameters.inputs.rotation,
                scale: this.parameters.inputs.scale,
            }),
            parameters: {
                vector: cyclesVec3(this.parameters.settings.vector),
                location: cyclesVec3WithNullableComponents(this.parameters.settings.location),
                rotation: cyclesVec3WithNullableComponents(this.parameters.settings.rotation),
                scale: cyclesVec3(this.parameters.settings.scale),
                mapping_type: cyclesMappingType(this.parameters.settings.vectorType),
            },
            resources: {},
        }

        return {vector: [mappingNode, "vector"]}
    }
}
